import { useState } from 'react';
import { CheckCircle2, Download, FileUp } from 'lucide-react';

import { Button, Checkbox, Drawer, ErrorAlert, Field, Table, Td, Th } from '@/components/ui';
import { downloadFile } from '@/lib/api-client';
import { useImportProducts, type ImportSummary } from '@/lib/catalog-api';
import { errorMessage } from '@/lib/utils';

/**
 * Bulk product import (spec §32).
 *
 * The flow is deliberately two-step: validate, look at what would happen, then
 * commit. The server writes nothing when any row fails, so the report below is
 * the whole story — there is never a partially applied file to reconcile.
 */
export function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [summary, setSummary] = useState<ImportSummary | null>(null);

  const importProducts = useImportProducts();

  const run = (dryRun: boolean): void => {
    if (!file) return;

    importProducts.mutate({ file, dryRun, updateExisting }, { onSuccess: setSummary });
  };

  const close = (): void => {
    setFile(null);
    setSummary(null);
    setUpdateExisting(false);
    importProducts.reset();
    onClose();
  };

  const hasErrors = (summary?.errors.length ?? 0) > 0;
  const validated = summary !== null && summary.dryRun && !hasErrors;
  const committed = summary !== null && !summary.dryRun && !hasErrors;

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Import products"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={close}>
            {committed ? 'Done' : 'Cancel'}
          </Button>

          {!committed && (
            <>
              <Button
                variant="outline"
                disabled={!file || importProducts.isPending}
                onClick={() => run(true)}
              >
                {importProducts.isPending ? 'Checking…' : 'Validate'}
              </Button>
              <Button
                // Committing is only offered once a dry run has come back clean,
                // so nobody imports 500 rows without seeing the damage first.
                disabled={!validated || importProducts.isPending}
                onClick={() => run(false)}
              >
                Import
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-muted">
          Upload a CSV using the same columns as the export. Existing products are matched by SKU.
        </p>

        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            void downloadFile(
              '/admin/catalog/products/import-template',
              'product-import-template.csv',
            )
          }
        >
          <Download className="size-4" aria-hidden="true" />
          Download template
        </Button>

        <Field label="CSV file" htmlFor="import-file">
          <label
            htmlFor="import-file"
            className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-line px-3 py-4 text-sm text-ink-muted hover:border-brand-500"
          >
            <FileUp className="size-4 shrink-0" aria-hidden="true" />
            {file ? file.name : 'Choose a .csv file…'}
            <input
              id="import-file"
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                // A new file invalidates the previous report.
                setSummary(null);
                importProducts.reset();
              }}
            />
          </label>
        </Field>

        <Checkbox
          label="Update products whose SKU already exists"
          checked={updateExisting}
          onChange={(event) => {
            setUpdateExisting(event.target.checked);
            setSummary(null);
          }}
        />

        {importProducts.isError && (
          <ErrorAlert title="The file could not be read">
            {errorMessage(importProducts.error)}
          </ErrorAlert>
        )}

        {summary && <ImportReport summary={summary} />}
      </div>
    </Drawer>
  );
}

function ImportReport({ summary }: { summary: ImportSummary }) {
  const hasErrors = summary.errors.length > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2 text-center">
        {[
          { label: 'Rows', value: summary.totalRows },
          { label: 'Create', value: summary.created },
          { label: 'Update', value: summary.updated },
          { label: 'Skip', value: summary.skipped },
        ].map((stat) => (
          <div key={stat.label} className="rounded-lg border border-line px-2 py-2">
            <p className="text-lg font-bold text-ink">{stat.value}</p>
            <p className="text-[11px] text-ink-subtle">{stat.label}</p>
          </div>
        ))}
      </div>

      {hasErrors ? (
        <>
          <ErrorAlert title={`${summary.errors.length} row(s) need fixing`}>
            Nothing was imported. Correct these rows and upload the file again.
          </ErrorAlert>

          <Table className="min-w-0">
            <thead>
              <tr>
                <Th>Row</Th>
                <Th>SKU</Th>
                <Th>Problem</Th>
              </tr>
            </thead>
            <tbody>
              {summary.errors.map((row) => (
                <tr key={row.rowNumber}>
                  <Td className="font-medium text-ink">{row.rowNumber}</Td>
                  <Td className="text-ink-muted">{row.sku ?? '—'}</Td>
                  <Td>
                    <ul className="space-y-0.5">
                      {row.errors.map((error, index) => (
                        <li key={index} className="text-xs text-danger-700">
                          <span className="font-medium">{error.field}</span>: {error.message}
                        </li>
                      ))}
                    </ul>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <div className="flex items-start gap-2 rounded-lg bg-success-50 px-3 py-2.5 text-sm text-success-700">
          <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p>
            {summary.dryRun
              ? 'Every row is valid. Choose Import to apply the file.'
              : 'Import complete. New products arrive as drafts — publish them when you are ready.'}
          </p>
        </div>
      )}
    </div>
  );
}
